import { z } from 'zod';
import { printAlignmentSchema } from './print-options';

/**
 * Previous results, printed beside the current one (requirements 7 and 8).
 *
 * Nested under `body`, because it is the results table — not a section of its
 * own. Adding it as a top-level section would let an operator place the history
 * somewhere the results are not, which is not a layout anybody wants.
 *
 * **`enabled` defaults to `false`.** Every print-template field is re-parsed on
 * the way *out* of Postgres, so a default of `true` would add two columns to
 * every stored sheet the moment this deploys — including sheets somebody has
 * already approved. The same rule the signatures section follows.
 */
export const printHistorySchema = z.object({
  enabled: z.boolean().default(false),

  /**
   * How many earlier visits to print.
   *
   * Two, which is what requirement 7 asks for. Four is the ceiling: past that
   * the parameter name has nowhere to go on A5, and `fittedWidths` is scaling
   * every column down to keep the table on the paper.
   */
  columns: z.coerce.number().int().min(1).max(4).default(2),

  /**
   * Column width in **millimetres**, never `'*'` or `'auto'`.
   *
   * A starred history column would compete with the parameter name for leftover
   * space and collapse it. A fixed width is also what lets `fittedWidths` scale
   * the whole table proportionally onto A5 rather than running a column off the
   * paper.
   */
  width: z.coerce.number().min(8).max(60).default(18),

  align: printAlignmentSchema.default('center'),

  /**
   * The heading over each history column.
   *
   * `{date}` is replaced with the visit's date. A label with no placeholder
   * gives every column the same heading, which is why the default has one.
   */
  label: z.string().trim().max(40).default('{date}'),

  /** Print the unit beside each previous value. Off: the current column has it. */
  show_unit: z.boolean().default(false),

  /**
   * Mark a previous value whose reference range differed from today's.
   *
   * A footnote under the table, not a tint. `fillColor` returning `undefined`
   * paints white over the watermark, and a tinted column reads as an alert with
   * no clinical rule behind it.
   */
  note_range_changes: z.boolean().default(true),

  /**
   * Include parameters no longer on the test.
   *
   * Off. A printed report lists what was tested *this* visit, and a row whose
   * current cell is blank reads as a missing result rather than as history.
   */
  include_retired: z.boolean().default(false),
});
export type PrintHistory = z.infer<typeof printHistorySchema>;
